import { Action, action, thunk, Thunk } from 'easy-peasy'
import isEmpty from 'lodash-es/isEmpty'
import { nameOfSquare, pieceSymbol } from '../../../chess/basics'
import { positionToFEN } from '../../../chess/fen'
import { addMoveToGTPosition, createRootGameTree, getMoveById, getPositionById, getStartingPosition, undoLastMove } from '../../../chess/gameTree'
import { createPuzzlePGNString, gameFromPGN } from '../../../chess/pgn'
import { BasePositionFEN } from '../../../chess/positionPresets'
import { Color, GameTree, GameTreePosition, Move } from '../../../chess/types'
import { Arrow, Square } from '../../../react-chessboard/src/chessboard/types'
import { GroupNames } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { ServerErrors } from '../../../sharedComponents/src/utils/serverErrors'
import { StoreModel } from '../../../store/store'
import { FtueOptions, getPracticePuzzle, getPuzzleTags, ITag, PuzzleStatus, resolvePracticePuzzle, usedHint } from '../../PuzzlesView/functions/puzzlesApi'
import { NotationsType } from '../components/Notations'
import { FeedbackType } from '../helpers/getFeedbackMessage'

const BOT_MOVING_DELAY = 1000 // 1 sec
const RETRY_MOVE_DELAY = 2500 // 2.5 sec
const FTUE_DELAY = 500 // 0.5 sec
const FTUE_PUZZLE_TAG = 'ftue'

interface IPuzzle {
    id: string
    pgnString: string
    fen: string
    rating: number
    description: string
    isFirstMove: boolean
    tags: Array<ITag>
}

interface Streaks {
    current: number,
    best: number,
    updatedAt: string,
}

export type PuzzlePracticeModel = {
    // State
    puzzleId: string
    puzzle?: IPuzzle
    progressGameTree: GameTree
    progressCurrentPosition: GameTreePosition
    gameTree: GameTree
    currentPosition: GameTreePosition
    myColor: Color
    flipped: boolean
    colorToMove: number
    allTags: Array<ITag>
    userRating: number
    hint?: Square
    hintsCounter: number
    availableHints: number
    customArrows: Array<Arrow>
    ftueOptions?: FtueOptions
    ftueCounter: number
    streaks: Streaks
    legalMove?: Square
    puzzleState: boolean | null
    currentTags: Array<number>
    opponentMoveTimeout?: NodeJS.Timeout
    retryMoveTimeout?: NodeJS.Timeout
    ftueTimeout?: NodeJS.Timeout
    attemptsCounter: number
    joinUsOpen: boolean
    morePuzzlesOpen: boolean
    showHintLimit: boolean
    hintDescription: string
    showBackdrop: boolean
    notations: Array<NotationsType>
    showSolution: boolean
    usedShowSolution: boolean
    disableNextButton: boolean
    showFinishFtueDialog: boolean
    showHintLimitDialog: boolean
    showSteakAnimation: boolean
    feedbackType?: FeedbackType

    // Actions
    setGameTree: Action<PuzzlePracticeModel, GameTree>
    setProgressCurrentPosition: Action<PuzzlePracticeModel, GameTreePosition>
    setCurrentPosition: Action<PuzzlePracticeModel, GameTreePosition>
    setFlipBoard: Action<PuzzlePracticeModel, boolean>
    initialiseGame: Action<PuzzlePracticeModel, { puzzleId: string, gameTree?: GameTree | undefined; isFirstMove?: boolean | undefined }>
    setColorToMove: Action<PuzzlePracticeModel, number>
    undoLastMove: Action<PuzzlePracticeModel, GameTree>
    setAllTags: Action<PuzzlePracticeModel, Array<ITag>>
    resetGame: Action<PuzzlePracticeModel>
    setUserRating: Action<PuzzlePracticeModel, number>
    setHint: Action<PuzzlePracticeModel, { from: Square, to?: Square } | undefined>
    clearHint: Action<PuzzlePracticeModel>
    setAvailableHints: Action<PuzzlePracticeModel, number>
    setFtueOptions: Action<PuzzlePracticeModel, FtueOptions | undefined>
    setFtueCounter: Action<PuzzlePracticeModel, number>
    setPuzzle: Action<PuzzlePracticeModel, IPuzzle | undefined>
    setStreaks: Action<PuzzlePracticeModel, Streaks>
    setLegalMove: Action<PuzzlePracticeModel, Square | undefined>
    setPuzzleState: Action<PuzzlePracticeModel, boolean | null>
    setCurrentTags: Action<PuzzlePracticeModel, Array<number>>
    setOpponentMoveTimeout: Action<PuzzlePracticeModel, NodeJS.Timeout>
    setRetryMoveTimeout: Action<PuzzlePracticeModel, NodeJS.Timeout>
    setFtueTimeout: Action<PuzzlePracticeModel, NodeJS.Timeout>
    setAttemptsCounter: Action<PuzzlePracticeModel, number>
    setJoinUsOpen: Action<PuzzlePracticeModel, boolean>
    setMorePuzzlesOpen: Action<PuzzlePracticeModel, boolean>
    setShowHintLimit: Action<PuzzlePracticeModel, boolean>
    setHintDescription: Action<PuzzlePracticeModel, string>
    setShowBackdrop: Action<PuzzlePracticeModel, boolean>
    setNotations: Action<PuzzlePracticeModel, Array<NotationsType>>
    updateNotations: Action<PuzzlePracticeModel, NotationsType>
    setShowSolution: Action<PuzzlePracticeModel, boolean>
    setUsedShowSolution: Action<PuzzlePracticeModel, boolean>
    setDisableNextButton: Action<PuzzlePracticeModel, boolean>
    setShowFinishFtueDialog: Action<PuzzlePracticeModel, boolean>
    setShowHintLimitDialog: Action<PuzzlePracticeModel, boolean>
    setShowSteakAnimation: Action<PuzzlePracticeModel, boolean>
    clearAllTimers: Action<PuzzlePracticeModel>
    setFeedbackType: Action<PuzzlePracticeModel, FeedbackType | undefined>

    // Thunks
    getTags: Thunk<PuzzlePracticeModel>
    getPuzzle: Thunk<PuzzlePracticeModel, undefined, undefined, StoreModel>
    sendPuzzle: Thunk<PuzzlePracticeModel>
    setHintAsync: Thunk<PuzzlePracticeModel, { from: Square, to?: Square } | undefined>
    handleMove: Thunk<PuzzlePracticeModel, Move>
    autoMove: Thunk<PuzzlePracticeModel, boolean | undefined>
    retryMove: Thunk<PuzzlePracticeModel>
    startPuzzle: Thunk<PuzzlePracticeModel>
    handleHint: Thunk<PuzzlePracticeModel, GroupNames | 'visitor'>
    handleNext: Thunk<PuzzlePracticeModel>
    startFtueFlow: Thunk<PuzzlePracticeModel>
    handleFtueStep: Thunk<PuzzlePracticeModel>
    resetAllStates: Thunk<PuzzlePracticeModel>
}

const initialGameTree = createRootGameTree(BasePositionFEN)
const initialCurrentPosition = getStartingPosition(initialGameTree)

export const PuzzlePracticeViewModel: PuzzlePracticeModel = {
    // Default state
    puzzleId: '',
    myColor: Color.White,
    flipped: false,
    progressGameTree: initialGameTree,
    progressCurrentPosition: initialCurrentPosition,
    gameTree: initialGameTree,
    currentPosition: initialCurrentPosition,
    colorToMove: 1,
    allTags: [],
    userRating: 0,
    hintsCounter: 0,
    availableHints: -1,
    customArrows: [],
    streaks: {
        current: 0,
        best: 0,
        updatedAt: new Date().toISOString(),
    },
    puzzleState: null,
    currentTags: [],
    opponentMoveTimeout: undefined,
    retryMoveTimeout: undefined,
    attemptsCounter: 1,
    joinUsOpen: false,
    morePuzzlesOpen: false,
    showHintLimit: false,
    hintDescription: '',
    showBackdrop: false,
    notations: [],
    showSolution: false,
    usedShowSolution: false,
    disableNextButton: false,
    showFinishFtueDialog: false,
    showHintLimitDialog: false,
    showSteakAnimation: false,
    ftueCounter: 0,

    // Actions
    setGameTree: action((state, payload) => {
        state.gameTree = payload
        state.currentPosition = getStartingPosition(payload)
    }),

    setProgressCurrentPosition: action((state, payload) => {
        state.progressCurrentPosition = payload
    }),

    setCurrentPosition: action((state, payload) => {
        state.currentPosition = payload
    }),

    setFlipBoard: action((state, flipped) => {
        state.flipped = flipped
    }),

    initialiseGame: action((state, gd) => {
        const newPuzzleGameTree = gd.gameTree || initialGameTree
        state.progressGameTree = newPuzzleGameTree
        const startingProgressPosition = getStartingPosition(newPuzzleGameTree)
        state.progressCurrentPosition = startingProgressPosition

        const newGameTree = createRootGameTree(positionToFEN(startingProgressPosition.position))
        state.gameTree = newGameTree
        state.currentPosition = getStartingPosition(newGameTree)

        if (gd.isFirstMove) {
            state.myColor = startingProgressPosition.position.turn
        } else if (startingProgressPosition.position.turn === Color.Black) {
            state.myColor = Color.White
        } else {
            state.myColor = Color.Black
        }

        state.flipped = state.myColor === Color.Black
        state.puzzleId = gd.puzzleId
    }),

    setColorToMove: action((state, number) => {
        state.colorToMove = number
    }),

    undoLastMove: action((state, payload) => {
        const previousPosition = undoLastMove(payload)
        state.currentPosition = previousPosition || initialCurrentPosition
        state.gameTree = payload
        state.notations = state.notations.slice(0, -1)
    }),

    setAllTags: action((state, tags) => {
        state.allTags = tags
    }),

    resetGame: action((state) => {
        state.myColor = Color.White
        state.progressGameTree = initialGameTree
        state.progressCurrentPosition = initialCurrentPosition
        state.gameTree = initialGameTree
        state.currentPosition = initialCurrentPosition
        state.colorToMove = 1
        state.puzzleId = ''
    }),

    setUserRating: action((state, rating) => {
        state.userRating = rating
    }),

    setHint: action((state, payload) => {
        if (payload) {
            state.hint = payload.from
            state.hintsCounter += 1
            if (payload.to) {
                state.customArrows.push([payload.from, payload.to, '#C9F1AD', 0.8])
            }
        } else {
            state.hint = undefined
            state.customArrows = []
        }
    }),

    clearHint: action((state) => {
        state.hint = undefined
        state.hintsCounter = 0
        state.customArrows = []
    }),

    setAvailableHints: action((state, payload) => {
        state.availableHints = payload
    }),

    setFtueOptions: action((state, payload) => {
        state.ftueOptions = payload
    }),

    setFtueCounter: action((state, payload) => {
        state.ftueCounter = payload
    }),

    setPuzzle: action((state, payload) => {
        state.puzzle = payload
    }),

    setStreaks: action((state, payload) => {
        state.streaks = payload
    }),

    setLegalMove: action((state, payload) => {
        state.legalMove = payload
    }),

    setPuzzleState: action((state, payload) => {
        state.puzzleState = payload
    }),

    setCurrentTags: action((state, payload) => {
        state.currentTags = payload
    }),

    setOpponentMoveTimeout: action((state, payload) => {
        state.opponentMoveTimeout = payload
    }),

    setRetryMoveTimeout: action((state, payload) => {
        state.retryMoveTimeout = payload
    }),

    setFtueTimeout: action((state, payload) => {
        state.ftueTimeout = payload
    }),

    setAttemptsCounter: action((state, payload) => {
        state.attemptsCounter = payload
    }),

    setJoinUsOpen: action((state, payload) => {
        state.joinUsOpen = payload
    }),

    setMorePuzzlesOpen: action((state, payload) => {
        state.morePuzzlesOpen = payload
    }),

    setShowHintLimit: action((state, payload) => {
        state.showHintLimit = payload
    }),

    setHintDescription: action((state, payload) => {
        state.hintDescription = payload
    }),

    setShowBackdrop: action((state, payload) => {
        state.showBackdrop = payload
    }),

    setNotations: action((state, payload) => {
        state.notations = payload
    }),

    updateNotations: action((state, payload) => {
        state.notations = state.notations.concat(payload)
    }),

    setShowSolution: action((state, payload) => {
        state.showSolution = payload
    }),

    setUsedShowSolution: action((state, payload) => {
        state.usedShowSolution = payload
    }),

    setDisableNextButton: action((state, payload) => {
        state.disableNextButton = payload
    }),

    setShowFinishFtueDialog: action((state, payload) => {
        state.showFinishFtueDialog = payload
    }),

    setShowHintLimitDialog: action((state, payload) => {
        state.showHintLimitDialog = payload
    }),

    setShowSteakAnimation: action((state, payload) => {
        state.showSteakAnimation = payload
    }),

    clearAllTimers: action((state) => {
        if (state.opponentMoveTimeout) {
            clearTimeout(state.opponentMoveTimeout)
            state.opponentMoveTimeout = undefined
        }
        if (state.retryMoveTimeout) {
            clearTimeout(state.retryMoveTimeout)
            state.retryMoveTimeout = undefined
        }
        if (state.ftueTimeout) {
            clearTimeout(state.ftueTimeout)
            state.ftueTimeout = undefined
        }
    }),

    setFeedbackType: action((state, payload) => {
        state.feedbackType = payload
    }),

    // Thunks
    setHintAsync: thunk(async (actions, payload, helpers) => {
        try {
            const { getState } = helpers
            const state = getState()
            if (state.availableHints !== 0) {
                const result = await usedHint()
                actions.setHint(payload)
                actions.setAvailableHints(result.availableHints)
                analyticsManager.dispatchEvent('powerUpUsed', {
                    type: 'hints',
                    oldArgument: state.availableHints,
                    newArgument: result.availableHints,
                    argumentChange: state.availableHints - result.availableHints,
                    feature: 'practicePuzzle',
                    puzzleId: state.puzzleId || '',
                })
            }
        } catch (error) {
            console.error('[PUZZLES] Error in setHintAsync:', error)
        }
    }),

    getTags: thunk(async (actions) => {
        try {
            const tagsData = await getPuzzleTags()
            if (tagsData?.tags) {
                actions.setAllTags(tagsData.tags)
            }
        } catch (error) {
            console.error('[PUZZLES] Error in getTags:', error)
        }
    }),

    getPuzzle: thunk(async (actions, payload, { getState, getStoreState }) => {
        try {
            const state = getState()
            const puzzleData = await getPracticePuzzle(state.currentTags)
            if (puzzleData?.puzzle) {
                const puzzle = puzzleData.puzzle
                const pgnString = createPuzzlePGNString(puzzle.startingFen, puzzle.moves)

                actions.setUserRating(puzzleData.userPuzzleRating || 0)
                actions.setPuzzle({
                    id: puzzle.id,
                    pgnString: pgnString,
                    fen: puzzle.startingFen,
                    rating: puzzle.rating,
                    description: puzzle.description,
                    isFirstMove: puzzle.isFirstMove,
                    tags: puzzle.tags || [],
                })

                if (puzzleData.streak) {
                    actions.setStreaks(puzzleData.streak)
                }

                if (puzzleData.options) {
                    actions.setAvailableHints(puzzleData.options.availableHints)
                }

                // Disable tags button if any tag includes "ftue" in its name
                if (puzzle.tags.some((tag: any) => tag.name.toLowerCase().includes(FTUE_PUZZLE_TAG))) {
                    analyticsManager.dispatchEvent('ftuePuzzleStarted', {
                        puzzleTag: puzzle.tags.map((tag: ITag) => tag.name).join(', '),
                        puzzleId: puzzle.id,
                    })

                    if (puzzleData.ftueOptions && Object.values(puzzleData.ftueOptions).some((value) => value === true)) {
                        actions.setFtueOptions(puzzleData.ftueOptions)
                    } else {
                        actions.setFtueOptions(undefined)
                    }
                } else {
                    actions.setFtueOptions(undefined)
                }

                actions.startPuzzle()

                analyticsManager.dispatchEvent('startedPuzzle', {
                    puzzleId: puzzle.id,
                    puzzleRating: puzzle.rating,
                    puzzleTags: puzzle.tags.map((tag: ITag) => tag.name).join(', '),
                    origin: 'practicePuzzle',
                    userPuzzleRating: puzzleData.userPuzzleRating || 0,
                })
            }
            if (puzzleData?.error?.code === ServerErrors.PRACTICE_PUZZLE_LIMIT_REACHED) {
                actions.setPuzzle(undefined)
                const store = getStoreState()
                const userData = store.userData.userData
                if (userData) {
                    const isGuest = userData.groups?.some((group: any) => group.name === GroupNames.GUEST)
                    const isMember = userData.groups?.some((group: any) => group.name === GroupNames.MEMBER)

                    if (isGuest) {
                        actions.setJoinUsOpen(true)
                    } else if (isMember) {
                        actions.setMorePuzzlesOpen(true)
                    }
                }
            }
            if (puzzleData?.error?.code === ServerErrors.PRACTICE_PUZZLE_FEATURE_DISABLED) {
                actions.setPuzzle(undefined)
                actions.setJoinUsOpen(true)
            }
        } catch (error) {
            console.error('[PUZZLES] Error in getPuzzle:', error)
        }
    }),

    sendPuzzle: thunk(async (actions, payload, { getState }) => {
        const state = getState()
        if (state.puzzle) {
            actions.setDisableNextButton(true)

            let status = PuzzleStatus.FAILED
            if (state.usedShowSolution) {
                status = PuzzleStatus.SHOWED
            } else if (state.puzzleState === true) {
                status = PuzzleStatus.PASSED
            } else if (state.puzzleState === null && state.hintsCounter === 0 && state.attemptsCounter === 1) {
                status = PuzzleStatus.SKIPPED
            }

            const data = await resolvePracticePuzzle({
                puzzleId: state.puzzle.id,
                status: status,
                usedHints: state.hintsCounter,
                attempts: state.attemptsCounter,
            })

            if (data.result) {
                if (state.puzzle.tags.some((tag: any) => tag.name.toLowerCase().includes(FTUE_PUZZLE_TAG))) {
                    analyticsManager.dispatchEvent('ftuePuzzleCompleted', {
                        puzzleTag: state.puzzle.tags.map((tag: any) => tag.name).join(', '),
                        puzzleId: state.puzzle.id,
                    })
                }

                if (data.ftueCompleted) {
                    actions.setShowFinishFtueDialog(true)
                }

                analyticsManager.dispatchEvent('completedPuzzle', {
                    origin: 'practicePuzzle',
                    puzzleId: state.puzzle.id,
                    puzzleRating: state.puzzle.rating,
                    puzzleSource: 0,
                    userPuzzleRating: state.userRating,
                    result: status,
                    attempts: state.attemptsCounter,
                    usedHints: state.hintsCounter,
                    datePlayed: new Date().toISOString(), // GTM expects this parameter
                })
                actions.setUserRating(data.userPuzzleRating || 0)
            }
            actions.setDisableNextButton(false)
        }
    }),

    handleMove: thunk((actions, move, { getState }) => {
        const state = getState()

        // clear ftue legal move
        if (state.ftueOptions?.highlightLegalMove) {
            actions.setLegalMove(undefined)
        }

        // if it not my turn, no interaction
        if (state.currentPosition.position.turn !== state.myColor) return false

        const expectedProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
        if (!expectedProgressMove) return false

        const isMoveCorrect =
            move.from === expectedProgressMove.move.from &&
            move.to === expectedProgressMove.move.to &&
            move.promotion === expectedProgressMove.move.promotion

        if (isMoveCorrect) {
            const nextProgressPosition = getPositionById(state.progressGameTree, expectedProgressMove.nextPositionId)
            actions.setProgressCurrentPosition(nextProgressPosition)
            const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, expectedProgressMove.move)
            actions.setCurrentPosition(nextPosition)

            const actualMove = getMoveById(state.gameTree, nextPosition.previousMoveId!)
            if (actualMove) {
                const moveFromSquare = nameOfSquare(actualMove.move.from)
                actions.updateNotations({
                    location: nameOfSquare(actualMove.move.to),
                    id: actualMove.id,
                    piece: pieceSymbol(state.currentPosition.position, actualMove.move.from),
                    playersMove: true,
                    usedHint: !!state.hint && state.hint === moveFromSquare,
                    usedShowSolution: false,
                    isLost: false,
                    isCorrect: true,
                    nextPositionId: nextPosition.id,
                })
            }

            if (nextProgressPosition.nextMoveIds.length === 0) {
                actions.setHint(undefined)
                actions.setPuzzleState(true)
                actions.setFeedbackType(FeedbackType.PUZZLE_SOLVED)
                actions.sendPuzzle()
            } else {
                actions.setHint(undefined)
                actions.setFeedbackType(FeedbackType.CORRECT_MOVE)
                const timeout = setTimeout(() => {
                    actions.autoMove()
                }, BOT_MOVING_DELAY)
                actions.setOpponentMoveTimeout(timeout)
            }

            return true
        }

        const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, move)
        actions.setCurrentPosition(nextPosition)

        const actualMove = getMoveById(state.gameTree, nextPosition.previousMoveId!)
        if (actualMove) {
            const moveFromSquare = nameOfSquare(actualMove.move.from)
            actions.updateNotations({
                location: nameOfSquare(actualMove.move.to),
                id: actualMove.id,
                piece: pieceSymbol(state.currentPosition.position, actualMove.move.from),
                playersMove: true,
                usedHint: !!state.hint && state.hint === moveFromSquare,
                usedShowSolution: false,
                isLost: true,
                isCorrect: false,
                nextPositionId: nextPosition.id,
            })
        }

        actions.setPuzzleState(false)
        actions.setFeedbackType(FeedbackType.INCORRECT_MOVE)
        actions.retryMove()
        return false
    }),

    autoMove: thunk((actions, showSolution = false, { getState }) => {
        const state = getState()
        const nextProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
        if (nextProgressMove) {
            const nextProgressPosition = getPositionById(state.progressGameTree, nextProgressMove.nextPositionId)
            actions.setProgressCurrentPosition(nextProgressPosition)
            const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, nextProgressMove.move)
            actions.setCurrentPosition(nextPosition)

            const actualMove = getMoveById(state.gameTree, nextPosition.previousMoveId!)
            if (actualMove) {
                const playersMove = state.currentPosition.position.turn === state.myColor
                const moveFromSquare = nameOfSquare(actualMove.move.from)
                actions.updateNotations({
                    location: nameOfSquare(actualMove.move.to),
                    id: actualMove.id,
                    piece: pieceSymbol(state.currentPosition.position, actualMove.move.from),
                    playersMove: playersMove,
                    usedHint: !!state.hint && state.hint === moveFromSquare,
                    usedShowSolution: playersMove && showSolution,
                    isLost: false,
                    isCorrect: playersMove && state.puzzleState !== false,
                    nextPositionId: nextPosition.id,
                })
            }

            if (showSolution) {
                actions.setHint(undefined)
                if (!state.showSolution) {
                    actions.setFeedbackType(FeedbackType.SOLUTION_SHOWN)
                }
                actions.setShowSolution(true)
                actions.setUsedShowSolution(true)
                actions.setDisableNextButton(true)
                const nextProgressPosition = getPositionById(state.progressGameTree, nextProgressMove.nextPositionId)
                if (nextProgressPosition.nextMoveIds.length === 0) {
                    actions.setPuzzleState(true)
                    actions.setShowSolution(false)
                    actions.setFeedbackType(FeedbackType.PUZZLE_SOLVED)
                    actions.sendPuzzle()
                } else {
                    const timeout = setTimeout(() => {
                        actions.autoMove(showSolution)
                    }, BOT_MOVING_DELAY)
                    actions.setOpponentMoveTimeout(timeout)
                }
            } else if (nextProgressPosition.position.turn === state.myColor) {
                const nextProgressPosition = getPositionById(state.progressGameTree, nextProgressMove.nextPositionId)
                if (nextProgressPosition.nextMoveIds.length === 0) {
                    actions.setPuzzleState(true)
                    actions.setFeedbackType(FeedbackType.PUZZLE_SOLVED)
                    actions.sendPuzzle()
                }
            }
        }
    }),

    retryMove: thunk((actions, payload, { getState }) => {
        const state = getState()
        actions.setAttemptsCounter(state.attemptsCounter + 1)
        const timeout = setTimeout(() => {
            actions.undoLastMove(state.gameTree)
            actions.setPuzzleState(null)
        }, RETRY_MOVE_DELAY)
        actions.setRetryMoveTimeout(timeout)
    }),

    startPuzzle: thunk((actions, payload, { getState }) => {
        const state = getState()
        if (state.puzzle) {
            const puzzleGameTree = gameFromPGN(state.puzzle.pgnString)
            actions.initialiseGame({ puzzleId: state.puzzle.id, gameTree: puzzleGameTree, isFirstMove: state.puzzle.isFirstMove })
            actions.setPuzzleState(null)

            // opponent first move
            if (!state.puzzle.isFirstMove) {
                const timeout = setTimeout(() => {
                    actions.autoMove()
                    actions.startFtueFlow()
                }, 1750)
                actions.setOpponentMoveTimeout(timeout)
            } else {
                actions.startFtueFlow()
            }
        }
    }),

    handleHint: thunk(async (actions, userType, { getState }) => {
        const state = getState()
        if (state.availableHints === 0) {
            // show hint limit
            if (userType === GroupNames.SUBSCRIBED) {
                actions.setShowHintLimit(true)
            } else {
                actions.setShowHintLimitDialog(true)
            }
        } else {
            // close hint ftue
            if (!isEmpty(state.ftueOptions) && (state.ftueOptions?.highlightHint || state.ftueOptions?.highlightSecondHint >= 0)) {
                actions.setShowBackdrop(false)
                actions.setHintDescription('')
            }

            // handle hint
            if (state.customArrows.length === 0) {
                const expectedProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
                if (expectedProgressMove && state.progressCurrentPosition.position.turn === state.myColor) {
                    const nextMoveFrom = nameOfSquare(expectedProgressMove?.move.from)
                    if (!state.hint) {
                        await actions.setHintAsync({ from: nextMoveFrom as Square })
                    } else {
                        const nextMoveTo = nameOfSquare(expectedProgressMove?.move.to)
                        await actions.setHintAsync({ from: nextMoveFrom as Square, to: nextMoveTo as Square })
                    }
                    actions.setFeedbackType(FeedbackType.HINT_USED)
                }
            }
        }
    }),

    handleNext: thunk(async (actions, _payload, { getState }) => {
        const state = getState()
        actions.setDisableNextButton(true)

        // skip puzzle manually
        if (!state.usedShowSolution && state.puzzleState !== true) {
            await actions.sendPuzzle()
        }

        actions.resetAllStates()
        await actions.getPuzzle()

        actions.setDisableNextButton(false)
    }),

    startFtueFlow: thunk((actions, payload, { getState }) => {
        const timeout = setTimeout(() => {
            const state = getState()
            const expectedProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
            if (expectedProgressMove && state.progressCurrentPosition.position.turn === state.myColor) {
                const nextMoveFrom = nameOfSquare(expectedProgressMove?.move.from)
                if (state.ftueOptions) {
                    if (state.ftueOptions.highlightLegalMove) {
                        actions.setLegalMove(nextMoveFrom as Square)
                    }
                    if (state.ftueOptions.highlightPiece) {
                        actions.setHint({ from: nextMoveFrom as Square })
                    }
                    if (state.ftueOptions.showArrow && state.ftueOptions.highlightSecondHint <= 0) {
                        const nextMoveTo = nameOfSquare(expectedProgressMove?.move.to)
                        actions.setHint({ from: nextMoveFrom as Square, to: nextMoveTo as Square })
                    }
                    if (state.ftueOptions.highlightHint && !state.showBackdrop) {
                        actions.setShowBackdrop(true)
                        actions.setHintDescription('Tap here for a hint!')
                    }
                    if (state.ftueOptions.highlightSecondHint === 0 && !state.showBackdrop) {
                        actions.setShowBackdrop(true)
                        actions.setHintDescription('Tap hint again to show move')
                    }
                    // if (state.ftueOptions.highlightDescription && !backdropShown && layout === Layout.MOBILE) {
                    //     setShowBackdrop(true)
                    //     setBackdropShown(true)
                    // }
                    actions.setFtueCounter(state.ftueCounter + 1)
                }
            }
        }, FTUE_DELAY)
        actions.setFtueTimeout(timeout)
    }),

    handleFtueStep: thunk((actions, _payload, { getState }) => {
        const timeout = setTimeout(() => {
            const state = getState()
            if (state.ftueOptions) {
                if (state.ftueCounter === state.ftueOptions.highlightSecondHint && !state.showBackdrop) {
                    // Show backdrop on second hint
                    actions.setShowBackdrop(true)
                    actions.setHintDescription('Tap hint again to show move')
                    actions.setFtueCounter(state.ftueCounter + 1)

                    // Show arrow after backdrop
                    const expectedProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
                    if (expectedProgressMove && state.progressCurrentPosition.position.turn === state.myColor) {
                        if (state.ftueOptions?.showArrow) {
                            const nextMoveFrom = nameOfSquare(expectedProgressMove?.move.from)
                            const nextMoveTo = nameOfSquare(expectedProgressMove?.move.to)
                            actions.setHint({ from: nextMoveFrom as Square, to: nextMoveTo as Square })
                        }
                    }
                }
            }
        }, FTUE_DELAY)
        actions.setFtueTimeout(timeout)
    }),

    resetAllStates: thunk((actions) => {
        actions.clearAllTimers()
        actions.resetGame()
        actions.clearHint()
        actions.setAttemptsCounter(1)
        actions.setShowSolution(false)
        actions.setUsedShowSolution(false)
        actions.setShowHintLimit(false)
        actions.setJoinUsOpen(false)
        actions.setMorePuzzlesOpen(false)
        actions.setPuzzleState(null)
        actions.setNotations([])
        actions.setShowBackdrop(false)
        actions.setHintDescription('')
        actions.setFtueCounter(0)
        actions.setLegalMove(undefined)
        actions.setShowFinishFtueDialog(false)
        actions.setShowHintLimitDialog(false)
        actions.setPuzzle(undefined)
        actions.setFeedbackType(undefined)
    }),
}
